import React, { useState } from 'react';
import { Edit2, Clock, Eye, Heart, BookOpen, Copy, Check, Building2, User, Image, PenLine, MessageSquare, ChevronRight, Plus, Wallet, ArrowRightLeft } from 'lucide-react';
import { useTheme } from '../../ThemeContext';
import AttestBadge from '../AttestBadge';


export default function ProfileView({
  authorName,
  walletAddress,
  walletCopied,
  copyWalletAddress,
  setShowChangeAvatarModal,
  setShowEditProfileModal,
  setShowAddLinkModal,
  socialLinks,
  deleteSocialLink,
  setShowAddAuthorModal,
  authors,
  selectedAuthor,
  setSelectedAuthor,
  organizations,
  setSelectedOrganization,
  setShowAddOrganizationModal,
  posts,
  walletConnected,
  disconnectWallet,
  onTransferWallet,
  activeTab,
  setActiveTab,
  getActivityContent,
  resetEditor,
  setView,
  viewPost
}) {
  const { t, isDark } = useTheme();
  const [selectedEntity, setSelectedEntity] = useState({ type: 'wallet', id: null });

  const myPosts = posts.filter(p => p.wallet_address === walletAddress);
  const totalViews = myPosts.reduce((sum, p) => sum + (p.views || 0), 0);
  const totalLikes = myPosts.reduce((sum, p) => sum + (p.like_count || 0), 0);

  const getRoleBadgeStyle = (role) => {
    switch(role) {
      case 'primary_owner':
        return { backgroundColor: t.buttonBg, color: t.buttonText };
      case 'owner':
        return { backgroundColor: isDark ? '#8b6914' : '#92400e', color: t.buttonText };
      case 'admin':
        return { backgroundColor: isDark ? '#374151' : '#f5f5f4', color: isDark ? '#d1d5db' : '#57534e' };
      case 'guest':
        return { backgroundColor: '#f5f5f4', color: '#78716c' };
      default:
        return { backgroundColor: t.borderStrong, color: t.text };
    }
  };

  const getRoleDisplayName = (role) => {
    switch(role) {
      case 'primary_owner': return 'Primary Owner';
      case 'owner': return 'Owner';
      case 'admin': return 'Admin';
      case 'guest': return 'Guest';
      default: return role;
    }
  };

  const isEntitySelected = (type, id = null) => {
    return selectedEntity.type === type && selectedEntity.id === id;
  };

  // Stat card component
  const StatCard = ({ icon: Icon, value, label, sublabel }) => (
    <div style={{
      padding: '20px',
      backgroundColor: t.surface,
      border: `1px solid ${t.borderStrong}`,
      borderRadius: '8px',
      display: 'flex',
      alignItems: 'flex-start',
      gap: '14px',
      transition: 'all 0.2s',
    }}>
      <div style={{
        width: '40px',
        height: '40px',
        borderRadius: '8px',
        backgroundColor: isDark ? 'rgba(254, 243, 199, 0.08)' : 'rgba(61, 36, 18, 0.06)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon size={18} style={{ color: t.buttonBg }} />
      </div>
      <div>
        <p style={{
          fontSize: '28px',
          fontWeight: '700',
          color: t.text,
          fontFamily: "'Poppins', sans-serif",
          lineHeight: 1.1,
          marginBottom: '2px',
        }}>
          {value}
        </p>
        <p style={{
          fontSize: '13px',
          color: t.textSecondary,
          fontFamily: "'Poppins', sans-serif",
          fontWeight: '500',
        }}>
          {label}
        </p>
      </div>
    </div>
  );

  // Sidebar entity button
  const EntityButton = ({ type, id, icon: Icon, name, subtitle, badge, onClick }) => {
    const selected = isEntitySelected(type, id);
    return (
      <button
        onClick={onClick}
        style={{
          width: '100%',
          padding: '12px 14px',
          backgroundColor: selected ? t.buttonBg : 'transparent',
          color: selected ? t.buttonText : t.text,
          borderRadius: '8px',
          border: 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          textAlign: 'left',
          fontFamily: "'Poppins', sans-serif",
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          if (!selected) e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.04)';
        }}
        onMouseLeave={(e) => {
          if (!selected) e.currentTarget.style.backgroundColor = 'transparent';
        }}
      >
        <div style={{
          width: '32px',
          height: '32px',
          borderRadius: '6px',
          backgroundColor: selected ? 'rgba(254, 243, 199, 0.2)' : (isDark ? 'rgba(255,255,255,0.06)' : 'rgba(61, 36, 18, 0.06)'),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}>
          <Icon size={15} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
            {name}
          </div>
          {subtitle && (
            <div style={{
              fontSize: '11px',
              opacity: 0.6,
              marginTop: '1px',
            }}>
              {subtitle}
            </div>
          )}
        </div>
        {badge && (
          <span style={{
            ...badge.style,
            padding: '2px 8px',
            borderRadius: '999px',
            fontSize: '10px',
            fontWeight: '600',
            flexShrink: 0,
            letterSpacing: '0.3px',
          }}>
            {badge.label}
          </span>
        )}
      </button>
    );
  };

  return (
    <div style={{
      maxWidth: '1200px',
      margin: '0 auto',
      padding: '32px 24px',
      fontFamily: "'Poppins', sans-serif",
    }}>
      <div style={{
        display: 'grid',
        gridTemplateColumns: '280px 1fr',
        gap: '28px',
        alignItems: 'start',
      }}>

        {/* ─── LEFT SIDEBAR ─── */}
        <div style={{
          position: 'sticky',
          top: '88px',
        }}>

          {/* Profile Card */}
          <div style={{
            backgroundColor: t.surface,
            border: `1px solid ${t.borderStrong}`,
            borderRadius: '12px',
            padding: '28px 20px 16px',
          }}>
            {/* Avatar */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              marginBottom: '20px',
            }}>
              <div style={{ position: 'relative', marginBottom: '14px' }}>
                <div style={{
                  width: '88px',
                  height: '88px',
                  borderRadius: '50%',
                  backgroundColor: t.buttonBg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '36px',
                  color: t.buttonText,
                  fontWeight: '700',
                  fontFamily: "'Poppins', sans-serif",
                }}>
                  {authorName ? authorName[0].toUpperCase() : 'A'}
                </div>
                <button
                  onClick={() => setShowChangeAvatarModal(true)}
                  style={{
                    position: 'absolute',
                    bottom: '-4px',
                    right: '-4px',
                    width: '28px',
                    height: '28px',
                    borderRadius: '6px',
                    backgroundColor: t.surface,
                    border: `1px solid ${t.borderStrong}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    transition: 'border-color 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.borderColor = t.accent || t.buttonBg}
                  onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderStrong}
                  title="Change avatar"
                >
                  <Image size={13} style={{ color: t.textSecondary }} />
                </button>
              </div>

              {/* Name */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                <h2 style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: t.text,
                  fontFamily: "'Poppins', sans-serif",
                  margin: 0,
                }}>
                  {authorName || 'Anonymous'}
                </h2>
                <button
                  onClick={() => setShowEditProfileModal(true)}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: t.textSecondary,
                    padding: '2px',
                    display: 'flex',
                    transition: 'color 0.2s',
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = t.text}
                  onMouseLeave={(e) => e.currentTarget.style.color = t.textSecondary}
                  title="Edit profile"
                >
                  <Edit2 size={13} />
                </button>
              </div>

              {/* Wallet Address */}
              <button
                onClick={copyWalletAddress}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px 8px',
                  borderRadius: '6px',
                  transition: 'background-color 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                title="Copy wallet address"
              >
                <span style={{
                  fontSize: '12px',
                  color: t.textSecondary,
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: '500',
                  letterSpacing: '0.2px',
                }}>
                  {walletAddress ? `${walletAddress.substring(0, 8)}...${walletAddress.substring(walletAddress.length - 6)}` : 'Not connected'}
                </span>
                {walletAddress && (
                  walletCopied
                    ? <Check size={12} style={{ color: '#16a34a' }} />
                    : <Copy size={12} style={{ color: t.textSecondary }} />
                )}
              </button>

              {/* Joined */}
              <p style={{
                fontSize: '11px',
                color: t.textSecondary,
                marginTop: '4px',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
              }}>
                <Clock size={11} />
                Joined {new Date().toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
              </p>
            </div>

            {/* Transfer Wallet + Disconnect */}
            {walletConnected && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <button
                  onClick={onTransferWallet}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${t.borderStrong}`,
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    color: t.textSecondary,
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = t.accent || t.buttonBg;
                    e.currentTarget.style.color = t.text;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = t.borderStrong;
                    e.currentTarget.style.color = t.textSecondary;
                  }}
                >
                  <ArrowRightLeft size={12} />
                  Transfer Wallet
                </button>
                <button
                  onClick={disconnectWallet}
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: `1px solid ${t.borderStrong}`,
                    borderRadius: '6px',
                    backgroundColor: 'transparent',
                    color: t.textSecondary,
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontFamily: "'Poppins', sans-serif",
                    fontWeight: '500',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = '#dc2626';
                    e.currentTarget.style.color = '#dc2626';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = t.borderStrong;
                    e.currentTarget.style.color = t.textSecondary;
                  }}
                >
                  Disconnect Wallet
                </button>
              </div>
            )}

            {/* ─── Entity Navigation ─── */}
            <div style={{
              marginTop: '20px',
              paddingTop: '20px',
              borderTop: `1px solid ${t.borderStrong}`,
            }}>
              {/* Dashboard link */}
              <EntityButton
                type="wallet"
                icon={Wallet}
                name="Dashboard"
                subtitle={`${myPosts.length} total posts`}
                onClick={() => setSelectedEntity({ type: 'wallet', id: null })}
              />

              {/* Authors Section */}
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${t.borderStrong}`,
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0 14px',
                  marginBottom: '8px',
                }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: t.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}>
                    Authors
                  </span>
                  <button
                    onClick={() => setShowAddAuthorModal(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: t.textSecondary,
                      padding: '2px',
                      display: 'flex',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = t.text}
                    onMouseLeave={(e) => e.currentTarget.style.color = t.textSecondary}
                    title="Add author"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {authors.length === 0 ? (
                  <p style={{
                    fontSize: '12px',
                    color: t.textSecondary,
                    padding: '8px 14px',
                    fontStyle: 'italic',
                  }}>
                    No author profiles yet
                  </p>
                ) : (
                  authors.map((author) => (
                    <EntityButton
                      key={author.id}
                      type="author"
                      id={author.id}
                      icon={User}
                      name={author.name}
                      subtitle={`${posts.filter(p => p.author_id === author.id).length} posts`}
                      onClick={() => {
                        setSelectedAuthor(author);
                        setSelectedEntity({ type: 'author', id: author.id });
                      }}
                    />
                  ))
                )}
              </div>

              {/* Organizations Section */}
              <div style={{
                marginTop: '16px',
                paddingTop: '16px',
                borderTop: `1px solid ${t.borderStrong}`,
              }}>
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '0 14px',
                  marginBottom: '8px',
                }}>
                  <span style={{
                    fontSize: '11px',
                    fontWeight: '600',
                    color: t.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.8px',
                  }}>
                    Organizations
                  </span>
                  <button
                    onClick={() => setShowAddOrganizationModal(true)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: t.textSecondary,
                      padding: '2px',
                      display: 'flex',
                      transition: 'color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.color = t.text}
                    onMouseLeave={(e) => e.currentTarget.style.color = t.textSecondary}
                    title="Create organization"
                  >
                    <Plus size={14} />
                  </button>
                </div>

                {!organizations || organizations.length === 0 ? (
                  <p style={{
                    fontSize: '12px',
                    color: t.textSecondary,
                    padding: '8px 14px',
                    fontStyle: 'italic',
                  }}>
                    No organizations yet
                  </p>
                ) : (
                  organizations.map((org) => (
                    <EntityButton
                      key={org.id}
                      type="organization"
                      id={org.id}
                      icon={Building2}
                      name={org.name}
                      subtitle={`${posts.filter(p => p.entity_type === 'organization' && p.entity_id === org.id).length} posts`}
                      badge={{
                        label: getRoleDisplayName(org.userRole),
                        style: getRoleBadgeStyle(org.userRole),
                      }}
                      onClick={() => {
                        setSelectedOrganization(org);
                        setSelectedEntity({ type: 'organization', id: org.id });
                      }}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ─── MAIN CONTENT ─── */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

          {/* Wallet Dashboard */}
          {selectedEntity.type === 'wallet' && (
            <>
              {/* Stats + Chart */}
              <div style={{
                backgroundColor: t.surface,
                border: `1px solid ${t.borderStrong}`,
                borderRadius: '12px',
                overflow: 'hidden',
              }}>
                {/* Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '0',
                }}>
                  {[
                    { icon: Eye, value: totalViews, label: 'Total Views' },
                    { icon: Heart, value: totalLikes, label: 'Total Likes' },
                    { icon: PenLine, value: myPosts.length, label: 'Posts' },
                    { icon: MessageSquare, value: 0, label: 'Comments' },
                  ].map((stat, idx) => (
                    <div key={stat.label} style={{
                      padding: '20px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '14px',
                      borderRight: idx < 3 ? `1px solid ${t.borderStrong}` : 'none',
                    }}>
                      <div style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '8px',
                        backgroundColor: isDark ? 'rgba(254, 243, 199, 0.08)' : 'rgba(61, 36, 18, 0.06)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <stat.icon size={18} style={{ color: t.buttonBg }} />
                      </div>
                      <div>
                        <p style={{
                          fontSize: '28px',
                          fontWeight: '700',
                          color: t.text,
                          fontFamily: "'Poppins', sans-serif",
                          lineHeight: 1.1,
                          marginBottom: '2px',
                        }}>
                          {stat.value}
                        </p>
                        <p style={{
                          fontSize: '13px',
                          color: t.textSecondary,
                          fontFamily: "'Poppins', sans-serif",
                          fontWeight: '500',
                        }}>
                          {stat.label}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Chart */}
                {myPosts.length > 0 && (
                  <div style={{
                    padding: '24px',
                    borderTop: `1px solid ${t.borderStrong}`,
                  }}>
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '20px',
                    }}>
                      <h3 style={{
                        fontSize: '15px',
                        fontWeight: '600',
                        color: t.text,
                        fontFamily: "'Poppins', sans-serif",
                        margin: 0,
                      }}>
                        Post Performance
                      </h3>
                      <span style={{
                        fontSize: '11px',
                        color: t.textSecondary,
                        fontWeight: '500',
                      }}>
                        Views per post
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      gap: '6px',
                      alignItems: 'flex-end',
                      height: '140px',
                      padding: '0 4px',
                    }}>
                      {(() => {
                        const chartPosts = myPosts.slice(0, 12);
                        const maxViews = Math.max(...chartPosts.map(p => p.views || 0), 1);
                        const chartHeight = 140;
                        return chartPosts.map((post, idx) => {
                          const barPx = Math.max(((post.views || 0) / maxViews) * chartHeight, 4);
                          const barOpacity = 0.75 + (0.25 * (barPx / chartHeight));
                          return (
                            <div
                              key={idx}
                              style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}
                            >
                              <div
                                title={`${post.title}: ${post.views || 0} views`}
                                style={{
                                  width: '100%',
                                  backgroundColor: t.buttonBg,
                                  height: `${barPx}px`,
                                  borderRadius: '4px 4px 0 0',
                                  opacity: barOpacity,
                                  transition: 'all 0.2s',
                                  cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scaleY(1.02)'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.opacity = String(barOpacity); e.currentTarget.style.transform = 'scaleY(1)'; }}
                              />
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
                )}
              </div>

              {/* Activity Feed */}
              <div style={{
                backgroundColor: t.surface,
                border: `1px solid ${t.borderStrong}`,
                borderRadius: '12px',
                overflow: 'hidden',
              }}>
                {/* Tabs */}
                <div style={{
                  display: 'flex',
                  gap: '0',
                  borderBottom: `1px solid ${t.borderStrong}`,
                  padding: '0 24px',
                }}>
                  {['ALL', 'POSTS', 'DRAFTS', 'LIKES', 'COMMENTS', 'SHARES'].map((tab) => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      style={{
                        padding: '14px 16px',
                        border: 'none',
                        backgroundColor: 'transparent',
                        fontSize: '12px',
                        fontWeight: '600',
                        color: tab === activeTab ? t.text : t.textSecondary,
                        cursor: 'pointer',
                        borderBottom: tab === activeTab ? `2px solid ${t.buttonBg}` : '2px solid transparent',
                        fontFamily: "'Poppins', sans-serif",
                        letterSpacing: '0.3px',
                        transition: 'color 0.2s',
                      }}
                      onMouseEnter={(e) => { if (tab !== activeTab) e.currentTarget.style.color = t.text; }}
                      onMouseLeave={(e) => { if (tab !== activeTab) e.currentTarget.style.color = t.textSecondary; }}
                    >
                      {tab}
                    </button>
                  ))}
                </div>

                {/* Content */}
                <div style={{ padding: '20px 24px' }}>
                  {getActivityContent() === null ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '48px 20px',
                    }}>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '12px',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                      }}>
                        <BookOpen size={28} style={{ color: t.textSecondary }} />
                      </div>
                      <p style={{
                        color: t.text,
                        fontSize: '15px',
                        fontWeight: '600',
                        marginBottom: '6px',
                        fontFamily: "'Poppins', sans-serif",
                      }}>
                        {activeTab.charAt(0) + activeTab.slice(1).toLowerCase()} coming soon
                      </p>
                    </div>
                  ) : getActivityContent().length === 0 ? (
                    <div style={{
                      textAlign: 'center',
                      padding: '48px 20px',
                    }}>
                      <div style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '12px',
                        backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        margin: '0 auto 16px',
                      }}>
                        <BookOpen size={28} style={{ color: t.textSecondary }} />
                      </div>
                      <p style={{
                        color: t.text,
                        fontSize: '15px',
                        fontWeight: '600',
                        marginBottom: '6px',
                        fontFamily: "'Poppins', sans-serif",
                      }}>
                        {activeTab === 'POSTS' || activeTab === 'ALL'
                          ? 'No posts yet'
                          : activeTab === 'DRAFTS'
                          ? 'No drafts'
                          : `No ${activeTab.toLowerCase()} yet`}
                      </p>
                      <p style={{
                        color: t.textSecondary,
                        fontSize: '13px',
                        marginBottom: '20px',
                      }}>
                        {activeTab === 'POSTS' || activeTab === 'ALL'
                          ? 'Your published work will appear here.'
                          : activeTab === 'DRAFTS'
                          ? 'Saved drafts will appear here.'
                          : `Your ${activeTab.toLowerCase()} activity will show up here.`}
                      </p>
                      {(activeTab === 'POSTS' || activeTab === 'ALL' || activeTab === 'DRAFTS') && (
                        <button
                          onClick={() => { resetEditor(); setView('editor'); }}
                          style={{
                            backgroundColor: t.buttonBg,
                            color: t.buttonText,
                            padding: '10px 24px',
                            borderRadius: '12px',
                            border: 'none',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: '600',
                            fontFamily: "'Poppins', sans-serif",
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.opacity = '0.9'; }}
                          onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.opacity = '1'; }}
                        >
                          Write Your First Post
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {getActivityContent().map((post) => (
                        <div
                          key={post.id}
                          style={{
                            border: `1px solid ${t.borderStrong}`,
                            borderRadius: '8px',
                            padding: '16px',
                            cursor: 'pointer',
                            display: 'flex',
                            gap: '16px',
                            transition: 'all 0.2s',
                          }}
                          onClick={() => viewPost(post)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                            e.currentTarget.style.borderColor = t.accent || t.buttonBg;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = 'none';
                            e.currentTarget.style.borderColor = t.borderStrong;
                          }}
                        >
                          {/* Thumbnail */}
                          <div style={{
                            width: '72px',
                            height: '72px',
                            borderRadius: '8px',
                            backgroundColor: post.featured_image_url ? 'transparent' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.04)'),
                            flexShrink: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden',
                          }}>
                            {post.featured_image_url ? (
                              <img
                                src={post.featured_image_url}
                                alt={post.title}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <Image size={20} style={{ color: t.textSecondary, opacity: 0.4 }} />
                            )}
                          </div>

                          {/* Content */}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <h4 style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              marginBottom: '4px',
                              color: t.text,
                              fontFamily: "'Poppins', sans-serif",
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}>
                              {post.title}
                            </h4>
                            {post.subtitle && (
                              <p style={{
                                fontSize: '13px',
                                color: t.textSecondary,
                                lineHeight: '1.4',
                                marginBottom: '8px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                                {post.subtitle}
                              </p>
                            )}
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '16px',
                              fontSize: '12px',
                              color: t.textSecondary,
                            }}>
                              <span>{new Date(post.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                              {post.status === 'draft' ? (
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '999px',
                                  backgroundColor: isDark ? 'rgba(212,165,116,0.15)' : 'rgba(254,243,199,0.8)',
                                  fontSize: '10px',
                                  fontWeight: '600',
                                  color: t.accent,
                                  letterSpacing: '0.03em',
                                }}>
                                  DRAFT
                                </span>
                              ) : (
                                <>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Eye size={12} /> {post.views || 0}
                                  </span>
                                  <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <Heart size={12} /> {post.like_count || 0}
                                  </span>
                                  {post.attest_tx_hash && (
                                    <AttestBadge txHash={post.attest_tx_hash} />
                                  )}
                                </>
                              )}
                              {post.category && (
                                <span style={{
                                  padding: '2px 8px',
                                  borderRadius: '999px',
                                  backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(61, 36, 18, 0.05)',
                                  fontSize: '10px',
                                  fontWeight: '500',
                                }}>
                                  {post.category}
                                </span>
                              )}
                            </div>
                          </div>

                          {post.status === 'draft' ? (
                            <PenLine size={16} style={{ color: t.accent, flexShrink: 0, alignSelf: 'center' }} />
                          ) : (
                            <ChevronRight size={16} style={{ color: t.textSecondary, flexShrink: 0, alignSelf: 'center' }} />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* Author View */}
          {selectedEntity.type === 'author' && (() => {
            const author = authors.find(a => a.id === selectedEntity.id);
            if (!author) return null;
            const authorPosts = posts.filter(p => p.author_id === author.id);
            const authorViews = authorPosts.reduce((sum, p) => sum + (p.views || 0), 0);
            const authorLikes = authorPosts.reduce((sum, p) => sum + (p.like_count || 0), 0);
            return (
              <>
                {/* Author Header */}
                <div style={{
                  backgroundColor: t.surface,
                  border: `1px solid ${t.borderStrong}`,
                  borderRadius: '12px',
                  padding: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '50%',
                      backgroundColor: t.buttonBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '20px',
                      color: t.buttonText,
                      fontWeight: '700',
                      fontFamily: "'Poppins', sans-serif",
                    }}>
                      {author.name[0].toUpperCase()}
                    </div>
                    <div>
                      <h3 style={{
                        fontSize: '18px',
                        fontWeight: '700',
                        color: t.text,
                        fontFamily: "'Poppins', sans-serif",
                        margin: 0,
                      }}>
                        {author.name}
                      </h3>
                      {author.bio && (
                        <p style={{ fontSize: '13px', color: t.textSecondary, marginTop: '2px' }}>
                          {author.bio}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedAuthor(author);
                      setView('authorProfile');
                    }}
                    style={{
                      padding: '8px 16px',
                      border: `1px solid ${t.borderStrong}`,
                      borderRadius: '6px',
                      backgroundColor: 'transparent',
                      color: t.text,
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: "'Poppins', sans-serif",
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = t.accent || t.buttonBg}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderStrong}
                  >
                    View Public Profile
                  </button>
                </div>

                {/* Author Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px',
                }}>
                  <StatCard icon={PenLine} value={authorPosts.length} label="Posts" />
                  <StatCard icon={Eye} value={authorViews} label="Views" />
                  <StatCard icon={Heart} value={authorLikes} label="Likes" />
                </div>

                {/* Author Posts */}
                <div style={{
                  backgroundColor: t.surface,
                  border: `1px solid ${t.borderStrong}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '16px 24px',
                    borderBottom: `1px solid ${t.borderStrong}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <h3 style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: t.text,
                      fontFamily: "'Poppins', sans-serif",
                      margin: 0,
                    }}>
                      Posts by {author.name}
                    </h3>
                    <span style={{ fontSize: '12px', color: t.textSecondary }}>
                      {authorPosts.length} {authorPosts.length === 1 ? 'post' : 'posts'}
                    </span>
                  </div>
                  <div style={{ padding: '20px 24px' }}>
                    {authorPosts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '12px',
                          backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.04)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                        }}>
                          <BookOpen size={28} style={{ color: t.textSecondary }} />
                        </div>
                        <p style={{ color: t.text, fontSize: '15px', fontWeight: '600', marginBottom: '6px' }}>No posts yet</p>
                        <p style={{ color: t.textSecondary, fontSize: '13px' }}>Posts published as {author.name} will appear here.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {authorPosts.map((post) => (
                          <div
                            key={post.id}
                            style={{
                              border: `1px solid ${t.borderStrong}`,
                              borderRadius: '8px',
                              padding: '16px',
                              cursor: 'pointer',
                              display: 'flex',
                              gap: '16px',
                              transition: 'all 0.2s',
                            }}
                            onClick={() => viewPost(post)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                              e.currentTarget.style.borderColor = t.accent || t.buttonBg;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.borderColor = t.borderStrong;
                            }}
                          >
                            <div style={{
                              width: '72px',
                              height: '72px',
                              borderRadius: '8px',
                              backgroundColor: post.featured_image_url ? 'transparent' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.04)'),
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}>
                              {post.featured_image_url ? (
                                <img src={post.featured_image_url} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Image size={20} style={{ color: t.textSecondary, opacity: 0.4 }} />
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h4 style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                marginBottom: '4px',
                                color: t.text,
                                fontFamily: "'Poppins', sans-serif",
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                                {post.title}
                              </h4>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                fontSize: '12px',
                                color: t.textSecondary,
                              }}>
                                <span>{new Date(post.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Eye size={12} /> {post.views || 0}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Heart size={12} /> {post.like_count || 0}</span>
                                {post.attest_tx_hash && (
                                  <AttestBadge txHash={post.attest_tx_hash} />
                                )}
                                {post.category && (
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(61, 36, 18, 0.05)',
                                    fontSize: '10px',
                                    fontWeight: '500',
                                  }}>
                                    {post.category}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight size={16} style={{ color: t.textSecondary, flexShrink: 0, alignSelf: 'center' }} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}

          {/* Organization View */}
          {selectedEntity.type === 'organization' && (() => {
            const org = organizations?.find(o => o.id === selectedEntity.id);
            if (!org) return null;
            const orgPosts = posts.filter(p => p.entity_type === 'organization' && p.entity_id === org.id);
            const orgViews = orgPosts.reduce((sum, p) => sum + (p.views || 0), 0);
            const orgLikes = orgPosts.reduce((sum, p) => sum + (p.like_count || 0), 0);
            return (
              <>
                {/* Org Header */}
                <div style={{
                  backgroundColor: t.surface,
                  border: `1px solid ${t.borderStrong}`,
                  borderRadius: '12px',
                  padding: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                    <div style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '8px',
                      backgroundColor: t.buttonBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: t.buttonText,
                    }}>
                      <Building2 size={22} />
                    </div>
                    <div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <h3 style={{
                          fontSize: '18px',
                          fontWeight: '700',
                          color: t.text,
                          fontFamily: "'Poppins', sans-serif",
                          margin: 0,
                        }}>
                          {org.name}
                        </h3>
                        <span style={{
                          ...getRoleBadgeStyle(org.userRole),
                          padding: '2px 8px',
                          borderRadius: '999px',
                          fontSize: '10px',
                          fontWeight: '600',
                          letterSpacing: '0.3px',
                        }}>
                          {getRoleDisplayName(org.userRole)}
                        </span>
                      </div>
                      {org.bio && (
                        <p style={{ fontSize: '13px', color: t.textSecondary, marginTop: '2px' }}>
                          {org.bio}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedOrganization(org);
                      setView('organizationSettings');
                    }}
                    style={{
                      padding: '8px 16px',
                      border: `1px solid ${t.borderStrong}`,
                      borderRadius: '6px',
                      backgroundColor: 'transparent',
                      color: t.text,
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: "'Poppins', sans-serif",
                      transition: 'all 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = t.accent || t.buttonBg}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = t.borderStrong}
                  >
                    Manage Organization
                  </button>
                </div>

                {/* Org Stats */}
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px',
                }}>
                  <StatCard icon={PenLine} value={orgPosts.length} label="Posts" />
                  <StatCard icon={Eye} value={orgViews} label="Views" />
                  <StatCard icon={Heart} value={orgLikes} label="Likes" />
                </div>

                {/* Org Posts */}
                <div style={{
                  backgroundColor: t.surface,
                  border: `1px solid ${t.borderStrong}`,
                  borderRadius: '12px',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    padding: '16px 24px',
                    borderBottom: `1px solid ${t.borderStrong}`,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <h3 style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      color: t.text,
                      fontFamily: "'Poppins', sans-serif",
                      margin: 0,
                    }}>
                      Posts by {org.name}
                    </h3>
                    <span style={{ fontSize: '12px', color: t.textSecondary }}>
                      {orgPosts.length} {orgPosts.length === 1 ? 'post' : 'posts'}
                    </span>
                  </div>
                  <div style={{ padding: '20px 24px' }}>
                    {orgPosts.length === 0 ? (
                      <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                        <div style={{
                          width: '64px',
                          height: '64px',
                          borderRadius: '12px',
                          backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.04)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          margin: '0 auto 16px',
                        }}>
                          <BookOpen size={28} style={{ color: t.textSecondary }} />
                        </div>
                        <p style={{ color: t.text, fontSize: '15px', fontWeight: '600', marginBottom: '6px' }}>No posts yet</p>
                        <p style={{ color: t.textSecondary, fontSize: '13px' }}>Posts published as {org.name} will appear here.</p>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {orgPosts.map((post) => (
                          <div
                            key={post.id}
                            style={{
                              border: `1px solid ${t.borderStrong}`,
                              borderRadius: '8px',
                              padding: '16px',
                              cursor: 'pointer',
                              display: 'flex',
                              gap: '16px',
                              transition: 'all 0.2s',
                            }}
                            onClick={() => viewPost(post)}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)';
                              e.currentTarget.style.borderColor = t.accent || t.buttonBg;
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = 'none';
                              e.currentTarget.style.borderColor = t.borderStrong;
                            }}
                          >
                            <div style={{
                              width: '72px',
                              height: '72px',
                              borderRadius: '8px',
                              backgroundColor: post.featured_image_url ? 'transparent' : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(61, 36, 18, 0.04)'),
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              overflow: 'hidden',
                            }}>
                              {post.featured_image_url ? (
                                <img src={post.featured_image_url} alt={post.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                              ) : (
                                <Image size={20} style={{ color: t.textSecondary, opacity: 0.4 }} />
                              )}
                            </div>
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <h4 style={{
                                fontSize: '15px',
                                fontWeight: '600',
                                marginBottom: '4px',
                                color: t.text,
                                fontFamily: "'Poppins', sans-serif",
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                              }}>
                                {post.title}
                              </h4>
                              <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '16px',
                                fontSize: '12px',
                                color: t.textSecondary,
                              }}>
                                <span>{new Date(post.timestamp).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Eye size={12} /> {post.views || 0}</span>
                                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><Heart size={12} /> {post.like_count || 0}</span>
                                {post.attest_tx_hash && (
                                  <AttestBadge txHash={post.attest_tx_hash} />
                                )}
                                {post.category && (
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '999px',
                                    backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(61, 36, 18, 0.05)',
                                    fontSize: '10px',
                                    fontWeight: '500',
                                  }}>
                                    {post.category}
                                  </span>
                                )}
                              </div>
                            </div>
                            <ChevronRight size={16} style={{ color: t.textSecondary, flexShrink: 0, alignSelf: 'center' }} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}